import { default as Input } from 'antd/es/input';
import type { InputProps } from 'antd/es/input';
import cn from 'classnames';
import type { Dispatch } from 'react';
import { useEffect, useMemo } from 'react';
import { getCryptoDecimals } from 'shared/src/cryptoUtils';

import { inputMoneyFormatter } from 'utils/formatters/inputMoneyFormatter';

import { useAntMask } from 'hooks/useAntMask';

type Props = {
  currency?: string | null;
  onChange?: Dispatch<string>;
  value?: string;
} & Omit<InputProps, 'onChange' | 'value'>;

/**
 * Custom Amount/Money input with formatting for readability
 */
export const AmountInput = ({
  currency,
  className,
  onChange = () => {},
  value,
  ...props
}: Props) => {
  const maximumFractionDigits = useMemo(
    () => getCryptoDecimals(currency ?? '') ?? 2,
    [currency]
  );

  const { setFormattedInput, mask } = useAntMask({
    validator: new RegExp(
      String.raw`^[\d,]+\.?[\d]{0,${maximumFractionDigits}}$`,
      'g'
    ),
    replacer: /,|(\.$)/g,
    separator: ',',
    formatter: inputMoneyFormatter,
    value,
    onChange
  });

  /**
   * Listen to change in maximum fraction digits
   * This will happen if the currency code changes from crypto to regular currency
   */
  useEffect(() => {
    setFormattedInput((value) => {
      if (value === undefined) {
        return undefined;
      }

      const [integer, fraction = ''] = value.split('.');

      // Early return if we are still within length limit
      if (fraction.length <= maximumFractionDigits) {
        return value;
      }

      const shortenedFraction = fraction.substring(0, maximumFractionDigits);
      return `${integer}.${shortenedFraction}`;
    });
  }, [maximumFractionDigits, setFormattedInput]);

  return (
    <Input
      {...mask}
      className={cn('w-full', className)}
      prefix={<span className="pr-1 font-bold">{currency}</span>}
      {...props}
    />
  );
};
